import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Flex, Spacer } from '@amzn/stencil-react-components/layout';
import { Label, Text } from '@amzn/stencil-react-components/text';
import { Select } from '@amzn/stencil-react-components/form';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { Table } from '@amzn/stencil-react-components/table';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import moment from 'moment';
import { QualityReportItem } from 'src/types/backendService';
import { getPerformanceWeekEnd, getPerformanceWeekEndForDate } from 'src/util/date_time_utils';

import {useGlobalContext, useUserDataContext} from '../hooks';
import { UnknownIdleTimeMetric } from '../types/backendService';

import { EmailReportWidget } from './shared/EmailReportWidget';
import {WA_WAREHOUSE_ALLOW_LIST} from "src/constants/featureGates";

const IconWithTooltip = withTooltip()(IconQuestionCircle);

export const ProductivityIdleTimeReport = ({}) => {
  const { t } = useTranslation();
  const { unknownIdleTimeMetrics } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  const feedbackLevelLabels: Record<string, string> = {
    INFO: t('info'),
    VERBAL_COACHING: t('verbalCoaching'),
    DOCUMENTED_COACHING: t('documentedCoaching'),
    FIRST_WRITTEN_WARNING: t('firstWrittenWarning'),
    SECOND_WRITTEN_WARNING: t('secondWrittenWarning'),
    FINAL_WRITTEN_WARNING: t('finalWrittenWarning'),
  };

  const unknownIdleTimeWeeklyAggregationMap = new Map();

  // we will show either 180 or 90 days. These are either 13 or 26 full weeks
  const weekLookback = WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId) ? 26 : 13;
  let currentWeek = getPerformanceWeekEnd();
  for (let i = 0; i <= weekLookback; i++) {
    unknownIdleTimeWeeklyAggregationMap.set(currentWeek.format('M/D/Y'), []); // Add the formatted week to the array
    currentWeek = currentWeek.subtract(1, 'week'); // Move to the previous week
  }

  if (unknownIdleTimeMetrics) {
    unknownIdleTimeMetrics.forEach((item) => {
      const week = getPerformanceWeekEndForDate(moment(item.timestamp! * 1000)).format('M/D/Y');
      if (unknownIdleTimeWeeklyAggregationMap.has(week)) {
        unknownIdleTimeWeeklyAggregationMap.get(week)!.push(item);
      }
    });
  }

  // Items are already sorted from newest to oldest. We will set the week to the first item
  const startingWeek = getPerformanceWeekEnd().format('M/D/Y');
  const startingReportItems = unknownIdleTimeWeeklyAggregationMap.get(startingWeek) ?? [];
  const [selectedWeek, setSelectedWeek] = useState(startingWeek);
  const [unknownIdleTimeWeeklyItems, setUnknownIdleTimeWeeklyItems] = useState<UnknownIdleTimeMetric[]>(startingReportItems);

  const updateState = (week: string) => {
    setSelectedWeek(week);
    setUnknownIdleTimeWeeklyItems(unknownIdleTimeWeeklyAggregationMap.get(week));
  };

  const formatFeedbackText = (feedback: String): string => {
    if (feedback) {
      const feedbackLevel = feedback.toUpperCase().split(' RECEIVED ON ');
      return `${feedbackLevelLabels[feedbackLevel[0]]} received on ${feedbackLevel[1]}`;
    }
    return '';
  };

  const IdleTimeDisclosure = ({}) => (
    <Text fontWeight="light">
      Your Unknown Idle Time (UIT) shows any periods of time when you were logged into a direct
      process path but were not actively processing items, including time when we would expect you
      not to be processing items. UIT will not result in feedback if it consists solely of periods
      when we expect you not to be processing items -- e.g., meal or rest breaks, reasonable
      bathroom breaks (including reasonable travel time), or fixing an issue on the floor. If your
      UIT is not excused or is only partially excused, and the remaining cumulative unexcused UIT
      exceeds 30 minutes, you may be subject to feedback. If you have further questions, please
      refer to the informational available in your WorkDocs folder in A to Z or talk to HR.
    </Text>
  );

  const columns = [
    {
      header: 'Date',
      accessor: ({ data }: { data: UnknownIdleTimeMetric }) => moment(data.timestamp! * 1000).utc().format('M/D/Y'),
    },
    {
      header: (
        <>
          Active Time
          {' '}
          <IconWithTooltip tooltipText="Time clocked in." />
        </>
      ),
      accessor: ({ data }: { data: UnknownIdleTimeMetric }) => (data.activeTime != undefined ? `${Math.floor(data.activeTime / 60)} Min` : '0 Min'),
    },
    {
      header: (
        <>
          Total Idle Time
          {' '}
          <IconWithTooltip tooltipText="Sum of all periods of time greater than 15min when you were logged into a direct process path but were not actively processing items." />
        </>
      ),
      accessor: ({ data }: { data: UnknownIdleTimeMetric }) => (data.totalIdleTime != undefined ? `${Math.floor(data.totalIdleTime / 60)} Min` : '0 Min'),
    },
    {
      header: 'Feedback',
      accessor: ({ data }: { data: UnknownIdleTimeMetric }) => (data.feedback === undefined ? '' : formatFeedbackText(data.feedback!)),
    },
  ];

  if (unknownIdleTimeMetrics === undefined) {
    return (
      <Flex
        height="188px"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner showText />
      </Flex>
    );
  }
  return (
    <Col gridGap="S200">
      <IdleTimeDisclosure />
      <Flex
        flexDirection="row"
        justifyContent="right"
        alignItems="center"
        gridGap="S300"
      >
        <Col
          alignItems="start"
          gridGap="S300"
        >
          <Label
            htmlFor="week-selector"
            id="week-selector-label"
          >
            <Text
              fontSize="T200"
              fontWeight="light"
              textAlign="right"
            >
              {t('weekEnding')}
            </Text>
          </Label>
        </Col>
        <Col width={150}>
          <Select
            labelId="week-selector-label"
            id="week-selector"
            value={selectedWeek}
            onChange={(value) => updateState(value)}
            options={Array.from(unknownIdleTimeWeeklyAggregationMap.keys())}
          />
        </Col>
        <Col>
          <EmailReportWidget reportType="NinetyDayIdleTimeReport" />
        </Col>
      </Flex>
      {unknownIdleTimeWeeklyItems.length === 0 ?
        <Text fontWeight="light">No data available.</Text> :
        <Col>
          <Table
            columns={columns}
            data={unknownIdleTimeWeeklyItems}
          />
        </Col>}
    </Col>
  );
};
